import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useRef } from 'react';
import {
	getAdFragment,
	getUserCity,
	getUserCountry,
	getUserLang,
	getUserType,
	themeMode,
	getUserCurrentCity,
	getUserLoadStatus,
	getUserEmail,
	getUserPhone,
	getUserTg,
	getUserWa,
	getUserID,
	getUserRegion,
	getUserTariff,
} from '@/stores/slices/userSlice';
import useEffectWindowSize from '../hooks/useEffectWindowSize';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { wordEndings } from '@/helper/wordEndings';
import {
	setAuthorizationStatus,
	setAviableForUsersStatus,
} from '@/stores/slices/popupSlice';
import { getIsMainPage } from '@/stores/slices/modelsState';
import { getIsAgencyPage } from '@/stores/slices/agencySlice';
import {
	getUserBalance,
	getUserTariffSlug,
	getUserHold,
	getEmailVerifyAt,
	getUserElitePrice,
} from '@/stores/slices/userSlice';
import { getIsLkPage } from '@/stores/slices/userSlice';
import {
	format,
	fromUnixTime,
	isValid,
	differenceInMinutes,
	differenceInHours,
	parseISO
} from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

// здесь собраны самые частоиспользуемые команды, чтобы не дублировать их каждый раз
const useLadyService = () => {
	const isJsdom =
		typeof window !== 'undefined' &&
		window?.navigator?.userAgent?.includes('jsdom');

	const { t } = useTranslation('translation');

	const token = localStorage.getItem('user');

	const dispatch = useDispatch();

	const navigate = useNavigate();

	// получить тип пользователя
	const userType = useSelector(getUserType);

	const userTypeAgency = userType === 'agency';
	const userTypePhoto = userType === 'photograph';
	const userTypeIndi = userType === 'indi';
	const userTypeCustomer = userType === 'customer';
	const userTypeAdmin = userType === 'admin';

	const isMainPage = useSelector(getIsMainPage);
	const isAgencyPage = useSelector(getIsAgencyPage);

	const userCity = useSelector(getUserCurrentCity);

	const userInfoStatus = useSelector(getUserLoadStatus);

	const emailVerifyAt = useSelector(getEmailVerifyAt);

	const userPhone = useSelector(getUserPhone);
	const userEmail = useSelector(getUserEmail);
	const userTg = useSelector(getUserTg);
	const userWa = useSelector(getUserWa);

	const userTariff = useSelector(getUserTariff);
	const userTariffSlug = useSelector(getUserTariffSlug);
	const userTariffSilver = userTariffSlug === 'silver';
	const userTariffGold = userTariffSlug === 'gold';
	const userTariffPlatinum = userTariffSlug === 'platinum';

	const elitePrice = useSelector(getUserElitePrice);

	// тема светлая/тёмная
	const theme = useSelector(themeMode);

	// получение языка
	const lang = useSelector(getUserLang);

	// получение города
	const city = useSelector(getUserCity);

	const region = useSelector(getUserRegion);
	const userBalance = useSelector(getUserBalance);
	const userBalanceHold = useSelector(getUserHold);
	const resultBalance = userBalance - userBalanceHold;
	const adFragment = useSelector(getAdFragment);

	const userId = useSelector(getUserID);

	// получение страны
	const country = useSelector(getUserCountry);

	// медиазапрос
	const windowWidth = useEffectWindowSize();


	// Универсальный форматтер: с таймзоной или без
	const formatTs = (created_at, pattern, timezone = null) => {
		const date = fromUnixTime(created_at);
		if (!isValid(date)) return '';

		return timezone
			? formatInTimeZone(date, timezone, pattern)
			: format(date, pattern);
	};

	// форматирования даты (12.05.2024 в 14:45)
	const formattedDate = (created_at, timezone = null) => {
		const date = formatTs(created_at, 'dd.MM.yyyy', timezone);
		const hours = formatTs(created_at, 'HH:mm', timezone);

		return `${date} ${t('at')} ${hours}`;
	};

	// форматирования даты (12.05.2024)
	const getDayMonthYear = (created_at, timezone = null) => {
		if (!created_at) return '';

		return formatTs(created_at, 'dd.MM.yyyy', timezone);
	};

	// парсинг таймстемпа (когда модель была онлайн)
	// Разницы (differenceIn*) — это мгновенные значения, таймзона на них не влияет.
	// Таймзона нужна только для проверки «сегодня / вчера».
	const getTimeAgo = (last_activity, timezone = null) => {
		const date = fromUnixTime(last_activity);
		const now = new Date();

		const dayPattern = 'yyyy-MM-dd';
		const today = formatTs(now.getTime() / 1000, dayPattern, timezone);
		const yesterday = formatTs(
			(now.getTime() - 86400 * 1000) / 1000,
			dayPattern,
			timezone,
		);
		const activityDay = formatTs(last_activity, dayPattern, timezone);

		const diffInMinutes = differenceInMinutes(now, date);
		const diffInHours = differenceInHours(now, date);

		if (diffInMinutes < 15) {
			return t('online');
		} else if (diffInMinutes < 60) {
			return `${t('came')} ${t('hoursago').toLowerCase()}`;
		} else if (activityDay === today) {
			return `${t('came')} ${t('today').toLowerCase()}`;
		} else if (activityDay === yesterday) {
			return `${t('came')} ${t('daysago').toLowerCase()}`;
		} else if (diffInHours < 72) {
			return `${t('came')} 3 ${t('days')} ${t('back').toLowerCase()}`;
		} else if (diffInHours < 96) {
			return `${t('came')} 4 ${t('days')} ${t('back').toLowerCase()}`;
		} else {
			return `${t('came')} ${t('thisweek').toLowerCase()}`;
		}
	};

	const TimeAgo = ({ timestamp, timeAgo, setTimeAgo, timezone = null }) => {
		const secondsSinceEpoch = parseInt(timestamp, 10);
		const date = fromUnixTime(secondsSinceEpoch);
		const now = new Date();

		// Разницы не зависят от таймзоны, но если нужно показать «сегодня/вчера» —
		// используйте formatTs(ts, 'yyyy-MM-dd', timezone) как в getTimeAgo.
		const diffInMinutes = differenceInMinutes(now, date);
		const diffInHours = differenceInHours(now, date);

		const pluralize = (count, words) => {
			const cases = [2, 0, 1, 1, 1, 2];
			return words[
				count % 100 > 4 && count % 100 < 20
					? 2
					: cases[count % 10 < 5 ? count % 10 : 5]
			];
		};

		if (diffInMinutes < 60) {
			setTimeAgo(`${diffInMinutes} ${t('minago')}`);
		} else if (diffInHours < 24) {
			setTimeAgo(
				`${diffInHours} ${pluralize(diffInHours, [
					t('hour'),
					t('hourfew'),
					t('hourmany'),
				])} ${t('back')}`,
			);
		} else {
			setTimeAgo(`${t('daysago')}`);
		}

		return timeAgo;
	};

	// из минут в часы/дни — чистая арифметика, таймзона не нужна
	const minutesToDhms = (minutes) => {
		minutes = Number(minutes);
		const d = Math.floor(minutes / (60 * 24));
		const h = Math.floor((minutes % (60 * 24)) / 60);
		const m = Math.floor(minutes % 60);

		const dDisplay =
			d > 0
				? `${d} ${wordEndings(d, [t('day'), t('days'), t('days')])} `
				: '';

		const hDisplay =
			h > 0
				? `${h} ${wordEndings(h, [
					t('hour'),
					t('hourfew'),
					t('hourmany'),
				])} `
				: '';

		const mDisplay = m > 0 ? `${m} ${t('min')}` : '';

		return dDisplay + hDisplay + mDisplay;
	};

	// конвертирует время из "10:00:00" в "10:00"
	// Если timeString — это ISO-строка с датой ("2024-05-12T10:00:00Z"),
	// можно передать timezone, чтобы время пересчиталось в нужный пояс.
	const convertTime = (timeString, timezone = null) => {
		if (!timeString) return '';

		if (timezone) {
			const parsed = /\d{4}-\d{2}-\d{2}/.test(timeString)
				? parseISO(timeString)
				: new Date(`1970-01-01T${timeString}`);

			if (isValid(parsed)) {
				return formatInTimeZone(parsed, timezone, 'HH:mm');
			}
		}

		const timeParts = timeString.split(':');
		return `${timeParts[0]}:${timeParts[1]}`;
	};

	const setAvialableForUser = () => {
		dispatch(setAuthorizationStatus(true));
		dispatch(setAviableForUsersStatus(true));
	};

	const usePrevious = (value) => {
		const ref = useRef();
		useEffect(() => {
			ref.current = value;
		});
		return ref.current;
	};

	const gendersTitle = {
		male: t('man'),
		trans: t('trans'),
		couple: t('couple'),
		female: t('woman'),
	};

	const orientations = {
		homosexual: t('homo'),
		lesbian: t('lesbi'),
		bisexual: t('bi'),
		straight: t('hetero'),
	};

	const travelVariants = {
		null: t('none'),
		no: t('no'),
		nearcity: t('nearcity'),
		incountry: t('incountry'),
		ineurope: t('ineurope'),
		inworld: t('inworld'),
	};

	const operatorAccess = {
		agencyprofileman: 'lk/agency',
		adsamanage: 'lk/profiles',
		promoandads: 'lk/profiles',
		reviewmanage: 'lk/reviews',
		partiesmanage: 'lk/createparty',
		statsaccess: 'lk/statistic',
		balancemanage: 'lk/balance',
		modelpreviews: 'lk/previews',
		bookings: 'lk/reservations',
		videochats: 'lk/videochats',
	};

	const checkAccessAndRedirect = (key) => {
		if (operatorAccess[key]) {
			// Если ключ найден, перенаправляем на нужную страницу
			window.location.href = `/${operatorAccess[key]}`;
		} else {
			// Если ключ не найден, перенаправляем на главную страницу
			window.location.href = '/';
		}
	};

	// Пример использования:
	const userAccessKey = 'statsaccess'; // Здесь может быть любое значение для проверки
	// checkAccessAndRedirect(userAccessKey);

	// задать формат изображения (xl,xxl, и т.д)
	// размеры (по высоте):
	// xl - 900
	// l - 700
	// m - 500
	// s - 360
	// xs-200
	// xxs-60
	function setMediaSize(media, size) {
		if (!!media) {
			const res = typeof media === 'string' ? media : media[0];
			// Разделяем URL на базовый (до имени файла) и имя файла с параметрами
			const urlParts = res?.split('/');
			if (!urlParts) {
				console.warn('NO URL PARTS in media: ', media);
				return null;
			}

			// Получаем файл + параметры из последней части URL
			const fileAndParams = urlParts.pop();

			// Вставляем размер перед расширением файла
			const modifiedFile = fileAndParams.replace(
				/(\.\w+)(\?.*)?$/,
				`/${size}$1$2`,
			);

			// Собираем обратно URL
			// console.log([...urlParts, modifiedFile].join('/'));
			return [...urlParts, modifiedFile].join('/');
		}
		console.error('NO media: ', media);
		return null;
	}

	const capitalizeFirstLetter = (str) => {
		return str.replace(/^./, (char) => char.toUpperCase());
	};

	const scrollToTop = (top = 0, left = 0) => {
		console.trace('!!! scroll to top');
		window.scrollTo({
			top: top,
			left: left,
			behavior: 'smooth',
		});
	};

	const scrollToHash = () => {
		const statusesHash = window.location.hash;
		if (statusesHash) {
			const element = document.querySelector(statusesHash);
			if (element) {
				setTimeout(() => {
					element.scrollIntoView({
						behavior: 'smooth',
						block: 'start',
					});
				}, 400);
			}
		}
	};

	const orderSelections = (selectedArr, orderToArr) => {
		const tagIndexMap = new Map(
			orderToArr.map((tag, index) => [tag, index]),
		);
		return [...selectedArr].sort(
			(a, b) =>
				(tagIndexMap.get(a) || Infinity) -
				(tagIndexMap.get(b) || Infinity),
		);
	};

	const deepCompare = (a1, a2) => {
		if (a1 === null || a2 === null) return false;

		if (Array.isArray(a1) && Array.isArray(a2)) {
			if (a1.length !== a2.length) return false;
			return a1.every((item, index) => deepCompare(item, a2[index]));
		}

		if (typeof a1 === 'object' && typeof a2 === 'object') {
			const keys1 = Object.keys(a1);
			const keys2 = Object.keys(a2);
			if (keys1.length !== keys2.length) return false;
			return keys1.every((key) => deepCompare(a1[key], a2[key]));
		}

		return a1 === a2;
	};

	const isLk = useSelector(getIsLkPage);

	return {
		capitalizeFirstLetter,
		elitePrice,
		emailVerifyAt,
		resultBalance,
		userBalanceHold,
		userBalance,
		isMainPage,
		isAgencyPage,
		isJsdom,
		token,
		dispatch,
		navigate,
		userType,
		userTypeAgency,
		userTypePhoto,
		userTypeIndi,
		userTypeCustomer,
		userTypeAdmin,
		userId,
		userCity,
		theme,
		lang,
		city,
		region,
		country,
		getTimeAgo,
		gendersTitle,
		orientations,
		minutesToDhms,
		convertTime,
		setAvialableForUser,
		formattedDate,
		getDayMonthYear,
		TimeAgo,
		adFragment,
		windowWidth,
		setMediaSize,
		t,
		userInfoStatus,
		userPhone,
		userEmail,
		userTg,
		userWa,
		userTariff,
		userTariffSlug,
		userTariffSilver,
		userTariffGold,
		userTariffPlatinum,
		travelVariants,
		scrollToTop,
		scrollToHash,
		isLk,
		usePrevious,
		orderSelections,
		deepCompare,
	};
};

export default useLadyService;
